import { ConnectionsDetailsFormPayload } from 'bank-connections/src/routes/ConnectionsDetails/types'
import { ConnectionsListFormPayload } from 'bank-connections/src/routes/ConnectionsList/types'
import { FilterCompanyContactsWebPage } from 'commons/service/bank/companyDetails'
import { BsaBatchDetailsOptions } from 'commons/types/DTO/bank/bsa'
import { CompanyListWebPageRequest } from 'commons/types/DTO/bank/companyList/CompanyListWebPageRequest'
import { LicenseWebPageRequest } from 'commons/types/DTO/bank/license'
import { QRWebPagePayload } from 'commons/types/DTO/bank/quarterlyReport'
import { MRWebPagePayload } from 'commons/types/DTO/bank/reportWebPage'
import { SalesDataSourceWebPagePayload } from 'commons/types/DTO/bank/salesDataSourceWebPage'
import { ServerOptions } from 'commons/types/api'
import { ContactListWebPageRequest } from 'commons/types/contracts/api/bank/contactList'

import { Atom } from './Atom'
import { Scope } from './Scope'
import { buildKey } from './buildKey'

export const Keys = {
  [Scope.ADVERSE_MEDIA_PUBLICATION]: {
    List: buildKey(Scope.ADVERSE_MEDIA_PUBLICATION, Atom.LIST),
    ListByCriteria: (opts?: ServerOptions) => buildKey(Scope.ADVERSE_MEDIA_PUBLICATION, Atom.LIST, opts)
  },
  [Scope.ADVERSE_MEDIA_SEARCH_CASE]: {
    ById: (id?: number) => buildKey(Scope.ADVERSE_MEDIA_SEARCH_CASE, Atom.ITEM, id)
  },
  [Scope.AFFILIATED_COMPANY]: {
    ById: (id: number) => buildKey(Scope.AFFILIATED_COMPANY, Atom.ITEM, id)
  },
  [Scope.ALERTS]: {
    WebPage: buildKey(Scope.ALERTS, Atom.LIST)
  },
  [Scope.BANK_APPLICATION]: {
    List: buildKey(Scope.BANK_APPLICATION, Atom.LIST),
    ListByCriteria: (opts: ServerOptions) => buildKey(Scope.BANK_APPLICATION, Atom.LIST, opts)
  },
  [Scope.APPLICATION_BANK_ACCOUNTS]: {
    RelationshipBankAccounts: (id: number) => buildKey(Scope.APPLICATION_BANK_ACCOUNTS, Atom.ITEM, id)
  },
  [Scope.BACK_OFFICE_STATISTICS]: {
    ListByDate: (date?: string) => buildKey(Scope.BACK_OFFICE_STATISTICS, Atom.LIST, { date })
  },
  [Scope.BANK]: {
    Settings: buildKey(Scope.BANK, Atom.ITEM, 'settings')
  },
  [Scope.CONNECTION]: {
    List: (payload?: ConnectionsListFormPayload) => buildKey(Scope.CONNECTION, Atom.LIST, payload)
  },
  [Scope.CONNECTION_DETAILS]: {
    List: (payload?: ConnectionsDetailsFormPayload) => buildKey(Scope.CONNECTION_DETAILS, Atom.LIST, payload)
  },
  [Scope.CLIENT_APPLICATION]: {
    List: buildKey(Scope.CLIENT_APPLICATION, Atom.LIST)
  },
  [Scope.COMPANY]: {
    ById: (id?: number) => buildKey(Scope.COMPANY, Atom.ITEM, id),
    List: buildKey(Scope.COMPANY, Atom.LIST),
    ListByIds: (ids?: number[]) => buildKey(Scope.COMPANY, Atom.LIST, { ids })
  },
  [Scope.COMPANY_DETAILS]: {
    WebPage: (id: number) => buildKey(Scope.COMPANY_DETAILS, Atom.ITEM, id)
  },
  [Scope.COMPANY_DETAILS_CONTACTS_LIST]: {
    List: (opts?: FilterCompanyContactsWebPage) => buildKey(Scope.COMPANY_DETAILS_CONTACTS_LIST, Atom.LIST, opts)
  },
  [Scope.CONFIGURATION]: {
    Item: buildKey(Scope.CONFIGURATION, Atom.ITEM)
  },
  [Scope.CONTACT]: {
    ById: (id: number) => buildKey(Scope.CONTACT, Atom.ITEM, id),
    List: (opts?: ContactListWebPageRequest) => buildKey(Scope.CONTACT, Atom.LIST, opts)
  },
  [Scope.DOCUMENT]: {
    ById: (id: number) => buildKey(Scope.DOCUMENT, Atom.ITEM, id),
    ExpiredList: (companyId: number) => buildKey(Scope.DOCUMENT, Atom.LIST, { companyId, type: 'expired' }),
    ListByCompanies: (companyIds: number[]) => buildKey(Scope.DOCUMENT, Atom.LIST, { companyIds })
  },
  [Scope.DOCUMENT_PERIOD]: {
    List: buildKey(Scope.DOCUMENT_PERIOD, Atom.LIST),
    ById: (id: number, documentId: number) => buildKey(Scope.DOCUMENT_PERIOD, Atom.ITEM, { id, documentId }),
    ListByDocument: (documentId: number) => buildKey(Scope.DOCUMENT_PERIOD, Atom.LIST, { documentId }),
    ListByCompanies: (companyIds: number[]) => buildKey(Scope.DOCUMENT_PERIOD, Atom.LIST, { companyIds })
  },
  [Scope.DOCUMENT_FILE]: {
    ById: (id: number) => buildKey(Scope.DOCUMENT_FILE, Atom.ITEM, id),
    List: buildKey(Scope.DOCUMENT_FILE, Atom.LIST),
    ListByPeriod: (periodId: number) => buildKey(Scope.DOCUMENT_FILE, Atom.LIST, { periodId }),
    ListByCompany: (companyId: number) => buildKey(Scope.DOCUMENT_FILE, Atom.LIST, { companyId }),
    ListByCompanies: (companyIds?: number[]) => buildKey(Scope.DOCUMENT_FILE, Atom.LIST, { companyIds })
  },
  [Scope.INTERNAL_TRANSFER]: {
    ById: (id?: number) => buildKey(Scope.INTERNAL_TRANSFER, Atom.ITEM, { id }),
    List: buildKey(Scope.INTERNAL_TRANSFER, Atom.LIST),
    ListByCriteria: (opts: ServerOptions) => buildKey(Scope.INTERNAL_TRANSFER, Atom.LIST, opts)
  },
  [Scope.INTERNAL_TRANSFER_EXPORT]: {
    ById: (id: number) => buildKey(Scope.INTERNAL_TRANSFER_EXPORT, Atom.ITEM, { id })
  },
  [Scope.INVOICE]: {
    ById: (id: number) => buildKey(Scope.INVOICE, Atom.ITEM, { id })
  },
  [Scope.LICENSE]: {
    ById: (id?: number) => buildKey(Scope.LICENSE, Atom.ITEM, { id }),
    Details: (filter?: LicenseWebPageRequest) => buildKey(Scope.LICENSE, Atom.ITEM, filter),
    List: (opts?: ServerOptions) => buildKey(Scope.LICENSE, Atom.LIST, opts)
  },
  [Scope.METRC_KEY]: {
    List: buildKey(Scope.METRC_KEY, Atom.LIST)
  },
  [Scope.MFA]: {
    Item: buildKey(Scope.MFA, Atom.ITEM)
  },
  [Scope.TEST]: {
    Test: buildKey(Scope.TEST, Atom.TEST)
  },
  [Scope.USER]: {
    Current: buildKey(Scope.USER, Atom.ITEM, 'current'),
    Session: buildKey(Scope.USER, Atom.ITEM, 'session'),
    ById: (id: number) => buildKey(Scope.USER, Atom.ITEM, id)
  },
  [Scope.USER_LIST]: {
    List: (searchTerm?: string | null, status?: string | null, page?: number | null, size?: number | null) =>
      buildKey(Scope.USER_LIST, Atom.LIST, ['user-list', searchTerm, status, page, size])
  },
  [Scope.RELATIONSHIPS]: {
    List: (filter?: CompanyListWebPageRequest) =>
      buildKey(Scope.RELATIONSHIPS, Atom.LIST, ['relationships-list', filter])
  },
  [Scope.WEB_PAGE_DOCUMENT]: {
    ItemByOpts: (opts: ServerOptions) => buildKey(Scope.WEB_PAGE_DOCUMENT, Atom.ITEM, opts),
    ListByCriteria: (opts: ServerOptions) => buildKey(Scope.WEB_PAGE_DOCUMENT, Atom.LIST, opts)
  },
  [Scope.BANK_PORTAL]: {
    Layout: buildKey(Scope.BANK_PORTAL, Atom.ITEM, ['layout'])
  },
  [Scope.CLIENT_PORTAL]: {
    Layout: buildKey(Scope.CLIENT_PORTAL, Atom.ITEM, ['layout'])
  },
  [Scope.MR]: {
    ById: (id: number) => buildKey(Scope.MR, Atom.ITEM, { id }),
    Details: (filter?: MRWebPagePayload) => buildKey(Scope.MR, Atom.ITEM, filter)
  },
  [Scope.QR]: {
    ItemByOpts: (opts?: QRWebPagePayload) => buildKey(Scope.MR, Atom.ITEM, { opts })
  },
  [Scope.SALES_DATA]: {
    ById: (id: number) => buildKey(Scope.SALES_DATA, Atom.ITEM, { id }),
    Details: (filter?: SalesDataSourceWebPagePayload) => buildKey(Scope.SALES_DATA, Atom.ITEM, filter)
  },
  [Scope.BSA]: {
    Summary: buildKey(Scope.BSA, Atom.ITEM, ['bsa-web-page']),
    BatchDetails: (filter?: BsaBatchDetailsOptions) => buildKey(Scope.BSA, Atom.ITEM, filter)
  },
  [Scope.BANK_DOCUMENT_ONBOARDING_SIGNERS]: {
    List: (opts) => buildKey(Scope.BANK_DOCUMENT_ONBOARDING_SIGNERS, Atom.LIST, { opts })
  }
}
